import type {
  FileInsights,
  HostInsights,
  InsightsKind,
  InsightsSubject,
  IpInsights,
  TechExposureResponse,
} from '../../app/modules/types/assetInsights'
import { apiClient } from '../client'

/**
 * Asset Insights — everything the two asset detail pages need about one asset.
 *
 * | kind   | page payload              | estate card                    |
 * |--------|---------------------------|--------------------------------|
 * | `host` | `/assets/{id}/insights/`  | `/assets/{id}/tech-exposure/`  |
 * | `ip`   | `/ip/{id}/insights/`      | `/ip/{id}/tech-exposure/`      |
 * | `file` | `/files/{id}/insights/`   | none — there is no endpoint    |
 *
 * Neither takes a query parameter. The payload is always "as of now" —
 * point-in-time views stay on the existing detail endpoint. A bad id is a 404,
 * deliberately indistinguishable from an asset in another organization.
 */

function base(kind: InsightsSubject): string {
  return kind === 'ip' ? '/ip' : kind === 'file' ? '/files' : '/assets'
}

export const assetInsightsApi = {
  /** The ten-block page payload. */
  getInsights: async (
    kind: InsightsSubject,
    id: string,
  ): Promise<HostInsights | IpInsights | FileInsights> => {
    const { data } = await apiClient.get(`${base(kind)}/${id}/insights/`)
    return data
  },

  /**
   * The estate card. A separate request because it is the one number on the
   * page whose cost grows with the ORGANIZATION rather than with the asset —
   * making the verdict wait for it would pay the estate's price to draw the
   * asset's grade.
   */
  getTechExposure: async (kind: InsightsKind, id: string): Promise<TechExposureResponse> => {
    const { data } = await apiClient.get(`${base(kind)}/${id}/tech-exposure/`)
    return data
  },
}
